import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import MarketOverview from './MarketOverview';

const Home = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerChildren = {
    visible: { 
      transition: { 
        staggerChildren: 0.2 
      } 
    }
  };

  const floatAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  return (
    <div>
      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={staggerChildren}
            ref={ref}
          >
            <motion.h1 
              className="hero-title"
              variants={fadeInUp}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Find Regulated & Registered Brokers with Instant Withdrawals
            </motion.h1>
            <motion.p 
              className="hero-subtitle"
              variants={fadeInUp}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Your trusted platform for finding reliable crypto trading brokers in today's demanding market
            </motion.p>
            <motion.div 
              className="hero-cta"
              variants={fadeInUp}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link to="/brokers" className="btn btn-brand">Explore Brokers</Link>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link to="/register" className="btn btn-outline">Get Started</Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Animated market demand visualization */}
        <div className="market-demand">
          <motion.div 
            className="demand-bar"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: "85%", opacity: 1 }}
            transition={{ duration: 2, delay: 0.5 }}
            whileHover={{ scale: 1.02 }}
          >
            <span className="demand-label">Market Demand: 85%</span>
          </motion.div>
          <motion.div 
            className="demand-bar"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: "92%", opacity: 1 }}
            transition={{ duration: 2, delay: 1 }}
            whileHover={{ scale: 1.02 }}
          >
            <span className="demand-label">Regulated Brokers: 92%</span>
          </motion.div>
          <motion.div 
            className="demand-bar"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: "78%", opacity: 1 }}
            transition={{ duration: 2, delay: 1.5 }}
            whileHover={{ scale: 1.02 }}
          >
            <span className="demand-label">Instant Withdrawals: 78%</span>
          </motion.div>
        </div>
      </section>
      
      {/* Live Market Overview Section */}
      <MarketOverview />
      
      <section className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={fadeInUp}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Why Choose UPM Traders</h2>
            <p>We provide the most reliable broker listings in the cryptocurrency market</p>
          </motion.div>
          
          <motion.div 
            className="features-grid staggered-animations"
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={staggerChildren}
          >
            <motion.div 
              className="feature" 
              variants={fadeInUp}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <motion.div 
                className="feature-icon"
                animate={floatAnimation}
                whileHover={{ 
                  scale: 1.1,
                  backgroundColor: "#D4AF37",
                  color: "#121F38"
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </motion.div>
              <h3>Regulated Brokers</h3>
              <p>We only list brokers regulated by top-tier financial authorities</p>
            </motion.div>
            
            <motion.div 
              className="feature" 
              variants={fadeInUp}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <motion.div 
                className="feature-icon"
                animate={floatAnimation}
                transition={{ delay: 0.2 }}
                whileHover={{ 
                  scale: 1.1,
                  backgroundColor: "#D4AF37",
                  color: "#121F38"
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="13 17 18 12 13 7"></polyline>
                  <polyline points="6 17 11 12 6 7"></polyline>
                </svg>
              </motion.div>
              <h3>Instant Withdrawals</h3>
              <p>Access your funds quickly with our verified instant withdrawal brokers</p>
            </motion.div>
            
            <motion.div 
              className="feature" 
              variants={fadeInUp}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <motion.div 
                className="feature-icon"
                animate={floatAnimation}
                transition={{ delay: 0.4 }}
                whileHover={{ 
                  scale: 1.1,
                  backgroundColor: "#D4AF37",
                  color: "#121F38"
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </motion.div>
              <h3>Crypto Trading</h3>
              <p>Specialized in cryptocurrency trading platforms with advanced tools</p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      <section className="market-stats">
        <div className="container">
          <motion.div 
            className="stats-grid staggered-animations"
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={staggerChildren}
          >
            <motion.div 
              className="stat-card" 
              variants={fadeInUp}
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h3>250+</h3>
              <p>Regulated Brokers</p>
            </motion.div>
            
            <motion.div 
              className="stat-card" 
              variants={fadeInUp}
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h3>98%</h3>
              <p>Client Satisfaction</p>
            </motion.div>
            
            <motion.div 
              className="stat-card" 
              variants={fadeInUp}
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h3>24/7</h3>
              <p>Support Available</p>
            </motion.div>
            
            <motion.div 
              className="stat-card" 
              variants={fadeInUp}
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <h3>15K+</h3>
              <p>Active Traders</p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      <section className="cta">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={fadeInUp}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Start Your Trading Journey Today</h2>
            <p>Join thousands of traders who found their perfect broker through our platform</p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Link to="/register" className="btn btn-brand">Get Started</Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;